import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useStore } from '@nanostores/react';
import { classNames } from '~/utils/classNames';
import FilePreview from './FilePreview';
import BrochurePreview from './BrochurePreview';
import PdfAttachmentPreview from './PdfAttachmentPreview';
import { UploadIntentDialog, type UploadIntent } from './UploadIntentDialog';
import type { BrochureAttachment } from '~/lib/pdf/brochure';
import { isPdfFile, type PdfDocAttachment } from '~/lib/pdf/pdf-text';
import { ScreenshotStateManager } from './ScreenshotStateManager';
import { ExpoQrModal } from '~/components/workbench/ExpoQrModal';
import type { ProviderInfo } from '~/types/model';
import type { DesignScheme } from '~/types/design-scheme';
import { Tooltip } from '~/components/ui/Tooltip';
import { TaskProgress } from './TaskProgress';
import type { TaskItem } from '~/types/context';
import { $selectedElement, clearSelection } from '~/lib/stores/element-selector';
import { DELETE_ELEMENT_INSTRUCTION } from '~/lib/element-selector/markers';
import { $composerUploadRequest } from '~/lib/stores/composerUpload';
import { useIsTouch } from '~/lib/hooks/useMediaQuery';
import { composerMaxHeight } from './composer-height';

/*
 * Project type. One kind today, so the hero renders no picker — a radio group
 * with a single option is a control that can't be operated. The type still
 * flows through props, so adding a second kind is a matter of widening this
 * union, adding its placeholders below, and restoring the pill row.
 */
export type ProjectTabId = 'landing';

// --- Typing placeholder suggestions per tab ---
const TAB_PLACEHOLDERS: Record<string, string[]> = {
  landing: [
    'Design a landing page for my AI startup...',
    'Create a product launch page with waitlist signup...',
    'Build a portfolio website with animations...',
    'Design a pricing page with feature comparison...',
    'Create a landing page for a mobile app...',
    'Build a coming soon page with email capture...',
    'Design a SaaS marketing page with testimonials...',
    'Create a restaurant website with menu and reservations...',
  ],
};

interface ChatBoxProps {
  isModelSettingsCollapsed: boolean;
  setIsModelSettingsCollapsed: (collapsed: boolean) => void;
  provider: any;
  providerList: any[];
  modelList: any[];
  isModelLoading: string | undefined;
  uploadedFiles: File[];
  imageDataList: string[];
  textareaRef: React.RefObject<HTMLTextAreaElement> | undefined;
  input: string;
  handlePaste: (e: React.ClipboardEvent) => void;
  TEXTAREA_MIN_HEIGHT: number;
  TEXTAREA_MAX_HEIGHT: number;
  isStreaming: boolean;
  handleSendMessage: (event: React.UIEvent, messageInput?: string) => void;
  isListening: boolean;
  startListening: () => void;
  stopListening: () => void;
  chatStarted: boolean;
  exportChat?: () => void;
  qrModalOpen: boolean;
  setQrModalOpen: (open: boolean) => void;
  handleFileUpload: (file?: File) => void;
  setProvider?: ((provider: ProviderInfo) => void) | undefined;
  model?: string | undefined;
  setModel?: ((model: string) => void) | undefined;
  setUploadedFiles?: ((files: File[]) => void) | undefined;
  setImageDataList?: ((dataList: string[]) => void) | undefined;
  handleInputChange?: ((event: React.ChangeEvent<HTMLTextAreaElement>) => void) | undefined;
  handleStop?: (() => void) | undefined;
  canResume?: boolean;
  handleResume?: () => void;
  enhancingPrompt?: boolean | undefined;
  enhancePrompt?: (() => void) | undefined;
  composerError?: string | null;
  onClearComposerError?: () => void;
  /** The current build plan. When present it renders as a header docked onto the top of the input box. */
  planTasks?: TaskItem[];
  chatMode?: 'discuss' | 'build';
  setChatMode?: (mode: 'discuss' | 'build') => void;
  designScheme?: DesignScheme;
  setDesignScheme?: (scheme: DesignScheme) => void;
  projectType?: ProjectTabId;
  setProjectType?: (type: ProjectTabId) => void;
  isStartingChat?: boolean;
  addToProject?: boolean[];
  onToggleAddToProject?: (index: number) => void;
  onRemoveFile?: (index: number) => void;
  brochure?: BrochureAttachment | null;
  onBrochureAttach?: (file: File) => void;
  onBrochureRemove?: () => void;
  pdfAttachments?: PdfDocAttachment[];
  onPdfAttach?: (file: File) => void;
  onPdfAttachRemove?: (file: File) => void;
}

export const ChatBox: React.FC<ChatBoxProps> = (props) => {
  const activeTab = props.projectType ?? 'landing'; // must match Chat.client.tsx default
  const selectedElement = useStore($selectedElement);
  // Touch keyboards have no Shift+Enter, so Enter inserts a newline there and Send is the button.
  const isTouch = useIsTouch();

  const hasInput = props.input.trim().length > 0 || props.uploadedFiles.length > 0;
  const sendDisabled =
    props.isStartingChat || !props.providerList || props.providerList.length === 0 || (!hasInput && !props.isStreaming);

  // Attachments (image chips + hero-only brochure chip) live inside the ONE
  // composer. The row animates open/closed so the composer grows and settles
  // back smoothly when files are added or removed.
  const hasImageAttachments = props.uploadedFiles.length > 0;
  const hasBrochureAttachment = !props.chatStarted && !!props.brochure;
  const hasPdfAttachment = !props.chatStarted && (props.pdfAttachments?.length ?? 0) > 0;
  const hasAttachments = hasImageAttachments || hasBrochureAttachment || hasPdfAttachment;

  // Content-driven height: when an attachment occupies the top of the composer,
  // collapse the empty textarea to a single line (ChatGPT/Claude-style) instead
  // of holding the tall hero min-height — that's what read as a big empty gap.
  // It still grows as the user types (the auto-resize effect below + in
  // Chat.client both size to scrollHeight, which respects this min-height).
  const textareaMinHeight = hasAttachments ? 48 : props.TEXTAREA_MIN_HEIGHT;

  // Re-run the textarea auto-resize when attachments toggle. Chat.client's
  // resize effect only fires on input changes, so adding/removing a file
  // wouldn't otherwise shrink/restore the box to the new min-height.
  useEffect(() => {
    const ta = props.textareaRef?.current;

    if (!ta) {
      return;
    }

    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, composerMaxHeight(props.TEXTAREA_MAX_HEIGHT))}px`;
  }, [hasAttachments]);

  // ─── Consolidated homepage upload (DEV-142) ───
  // The hero used to expose two indistinguishable buttons — a generic attachment
  // and a brochure→website upload. Now there's one entry point: pick a file, and
  // a PDF (the only ambiguous case — the brochure pipeline is PDF-only) prompts an
  // intent dialog. Images have no brochure meaning, so they attach directly.
  //
  // The two PDF modes are mutually exclusive:
  //   • Brochure (PDF→site) is a single, locked PDF — no other PDF can be added.
  //   • Standard attachments stack — many content PDFs, but no brochure.
  // So the intent dialog only ever shows for the FIRST PDF; after that the mode
  // is fixed and later PDFs either attach as standard or are blocked (brochure).
  // Images are always allowed in either mode.
  const brochureEnabled = !props.chatStarted && !!props.onBrochureAttach;
  const [pendingPdf, setPendingPdf] = useState<File | null>(null);
  const hasBrochure = !!props.brochure;
  const hasStandardPdf = (props.pdfAttachments?.length ?? 0) > 0;

  const routePickedFile = (file: File) => {
    const isPdf = isPdfFile(file);

    // Images (and any non-PDF) can only be standard attachments — always allowed.
    if (!isPdf || !brochureEnabled) {
      props.handleFileUpload(file);
      return;
    }

    // A brochure locks out every other PDF — it's the single design source.
    if (hasBrochure) {
      toast.error(
        `You can't add another PDF — "${props.brochure?.file.name}" is building your site. Remove it first to attach a different PDF.`,
      );
      return;
    }

    // Already in standard mode → this PDF is another content attachment; no dialog.
    if (hasStandardPdf) {
      props.onPdfAttach?.(file);
      return;
    }

    // First PDF this chat — ask what it's for (brochure vs content).
    setPendingPdf(file);
  };

  const openHomepageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];

      if (file) {
        routePickedFile(file);
      }
    };

    input.click();
  };

  /*
   * Requests raised outside the composer (the hero's brochure line, the hero
   * drop target) run through the same routing as the paperclip, so a PDF still
   * gets the brochure-or-attachment question and the locked-mode rules hold.
   * Ignored mid-chat, where those surfaces don't exist.
   */
  const uploadRequest = useStore($composerUploadRequest);
  const lastHandledUpload = React.useRef(uploadRequest.seq);

  useEffect(() => {
    if (uploadRequest.seq === lastHandledUpload.current) {
      return;
    }

    lastHandledUpload.current = uploadRequest.seq;

    if (props.chatStarted) {
      return;
    }

    if (uploadRequest.file) {
      routePickedFile(uploadRequest.file);
    } else {
      openHomepageUpload();
    }
  }, [uploadRequest]);

  const resolveIntent = (intent: UploadIntent) => {
    const file = pendingPdf;
    setPendingPdf(null);

    if (!file) {
      return;
    }

    if (intent === 'brochure') {
      props.onBrochureAttach?.(file);
    } else {
      // Standard intent for a PDF: read it as text (the dialog only opens for PDFs).
      props.onPdfAttach?.(file);
    }
  };

  return (
    <div className="ui-root relative w-full max-w-chat mx-auto z-prompt">
      {/* ─── Selected element chip (above composer) ─── */}
      {selectedElement && (
        <div className="flex items-center gap-2 px-2.5 py-1.5 mb-2 rounded-lg bg-raised border border-hairline text-[11.5px]">
          <span className="font-geist-mono text-secondary truncate">
            {selectedElement.tag} · {selectedElement.file.split('/').pop()}:{selectedElement.line}
          </span>
          <div className="flex-1" />
          <Tooltip content="Delete this element">
            <button
              type="button"
              onClick={(event) => props.handleSendMessage?.(event, DELETE_ELEMENT_INSTRUCTION)}
              className="ui-btn-icon hover:text-danger"
              aria-label="Delete this element"
            >
              <span className="i-ph:trash text-[13px]" />
            </button>
          </Tooltip>
          <Tooltip content="Clear selection">
            <button type="button" onClick={() => clearSelection()} className="ui-btn-icon" aria-label="Clear selection">
              <span className="i-ph:x text-[13px]" />
            </button>
          </Tooltip>
        </div>
      )}

      {/* ─── Agent status indicator (above composer, in-chat only) ───
          The hero "Starting…" state renders INSIDE the composer, below the
          prompt (see the inline generation block in the composer wrapper). */}
      {props.chatStarted && (
        <div className="flex items-center gap-2 px-1 pb-2">
          {props.isStreaming ? (
            <>
              <span className="ui-dot ui-dot-building ui-pulsing" />
              <span className="text-[11px] text-warning font-geist font-medium">Working…</span>
            </>
          ) : (
            <>
              <span className="ui-dot ui-dot-ok" />
              <span className="text-[11px] text-success font-geist font-medium">Ready</span>
            </>
          )}
        </div>
      )}

      {/* ─── Plan, docked onto the composer (below the status, above the input) ─── */}
      {!!props.planTasks?.length && (
        <div className="-mb-px relative z-[1]">
          <TaskProgress tasks={props.planTasks} attached />
        </div>
      )}

      {/* Composer wrapper — focus-within lifts the border to prominent + a faint
          lime ring, so the active input reads clearly (DESIGN.md input focus). */}
      <div
        className={classNames(
          'ui-composer transition-[border-color,box-shadow] focus-within:border-prominent focus-within:shadow-[0_0_0_3px_rgba(217,242,106,0.07)]',
          !!props.planTasks?.length && '!rounded-t-none',
        )}
      >
        {/* ─── Attachments row (compact chips, inside composer top) ───
            Images + hero brochure share one row so uploads read as native
            attachments on the single input, never a separate card above it.
            AnimatePresence gives the 150–200ms grow/settle on add/remove. */}
        <AnimatePresence initial={false}>
          {hasAttachments && (
            <motion.div
              key="composer-attachments"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="overflow-hidden -mt-0.5"
            >
              <div className="flex flex-wrap items-start gap-2.5 pb-2">
                <FilePreview
                  files={props.uploadedFiles}
                  imageDataList={props.imageDataList}
                  onRemove={(index) => {
                    if (props.onRemoveFile) {
                      props.onRemoveFile(index);
                    } else {
                      props.setUploadedFiles?.(props.uploadedFiles.filter((_, i) => i !== index));
                      props.setImageDataList?.(props.imageDataList.filter((_, i) => i !== index));
                    }
                  }}
                  addToProject={props.addToProject}
                  onToggleAddToProject={props.onToggleAddToProject}
                />
                {!props.chatStarted && (
                  <BrochurePreview brochure={props.brochure ?? null} onRemove={() => props.onBrochureRemove?.()} />
                )}
                {!props.chatStarted &&
                  props.pdfAttachments?.map((att, i) => (
                    <PdfAttachmentPreview
                      key={`${att.file.name}-${i}`}
                      attachment={att}
                      onRemove={() => props.onPdfAttachRemove?.(att.file)}
                    />
                  ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Prompt slot ───
            While editing, the textarea. After Send the generation status
            OVERLAYS the textarea in place: the textarea stays mounted so its
            slot height is preserved and its text is hidden — the status appears
            exactly where the prompt was, with zero layout shift. ─── */}
        <div className="relative">
          {!props.input && !props.chatStarted && !props.isStartingChat && !props.isListening && (
            <TypingPlaceholder activeTab={activeTab} />
          )}
          {/* Listening overlay — unambiguous "mic is live" signal while the input is
              still empty; once words stream in, the pulsing mic chip carries the state. */}
          {props.isListening && !props.input && (
            <div className="absolute inset-0 pointer-events-none px-1 py-0.5 flex items-start">
              <span className="inline-flex items-center gap-2 leading-[1.55]">
                <span className="ui-dot ui-dot-error ui-pulsing" />
                <span className="font-geist text-[13px] text-secondary">Listening — start speaking…</span>
              </span>
            </div>
          )}
          <textarea
            ref={props.textareaRef}
            disabled={props.isStartingChat}
            className={classNames(
              'w-full bg-transparent text-primary font-geist text-[14px] leading-[1.55] outline-none resize-none px-1 py-0.5 border-0 relative z-[1]',
              props.isStartingChat && 'opacity-0 pointer-events-none',
            )}
            onDragEnter={(e) => {
              e.preventDefault();
              e.currentTarget.style.boxShadow = '0 0 0 1px var(--accent-glow, #D9F26A)';
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.boxShadow = '0 0 0 1px var(--accent-glow, #D9F26A)';
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.boxShadow = 'none';
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.boxShadow = 'none';

              const files = Array.from(e.dataTransfer.files);
              files.forEach((file) => {
                if (file.type.startsWith('image/')) {
                  const reader = new FileReader();

                  reader.onload = (ev) => {
                    const base64Image = ev.target?.result as string;
                    props.setUploadedFiles?.([...props.uploadedFiles, file]);
                    props.setImageDataList?.([...props.imageDataList, base64Image]);
                  };
                  reader.readAsDataURL(file);
                }
              });
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                if (event.shiftKey || isTouch) {
                  return;
                }

                event.preventDefault();

                if (props.isStreaming) {
                  props.handleStop?.();
                  return;
                }

                if (event.nativeEvent.isComposing) {
                  return;
                }

                props.handleSendMessage?.(event);
              }
            }}
            value={props.input}
            onChange={(event) => props.handleInputChange?.(event)}
            onPaste={props.handlePaste}
            style={{
              minHeight: textareaMinHeight,
              maxHeight: composerMaxHeight(props.TEXTAREA_MAX_HEIGHT),
            }}
            placeholder={props.chatStarted ? 'Reply to Dunea, or paste a screenshot…' : ''}
            translate="no"
            rows={2}
          />
          {/* Generation status overlays the hidden textarea — same slot, zero shift */}
          {props.isStartingChat && !props.chatStarted && (
            <div className="absolute inset-0 z-[2] flex flex-col justify-center gap-0.5 px-1">
              <div className="flex items-center gap-2">
                <span className="ui-dot ui-dot-live ui-pulsing" />
                <span className="text-[11px] text-accent font-geist font-medium">Starting…</span>
              </div>
              <span className="text-[11px] text-muted font-geist pl-[14px]">
                {props.brochure ? 'Initializing brochure analysis…' : 'Setting up your workspace…'}
              </span>
            </div>
          )}
        </div>

        {/* ─── Inline composer error (enhance / voice failures) ───
            Renders right above the buttons that caused it — no toasts. */}
        <AnimatePresence initial={false}>
          {props.composerError && (
            <motion.div
              key="composer-error"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <div className="flex items-center gap-2 mt-1.5 px-1" role="alert">
                <span className="ui-dot ui-dot-error flex-shrink-0" />
                <span className="font-geist text-[11.5px] text-danger">{props.composerError}</span>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => props.onClearComposerError?.()}
                  className="ui-btn-icon flex-shrink-0"
                  aria-label="Dismiss error"
                >
                  <span className="i-ph:x text-[11px]" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Toolbar row ─── */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 mt-1.5 min-w-0">
          {/* Attach file — one entry point. Mid-chat it's an image picker; on the
              hero it also accepts a PDF and asks whether that PDF is a plain
              attachment or a brochure to build a site from (DEV-142). */}
          <Tooltip
            content={
              brochureEnabled
                ? 'Attach an image, or a brochure PDF to build the site from'
                : 'Attach an image or screenshot'
            }
          >
            <button
              type="button"
              onClick={() => (brochureEnabled ? openHomepageUpload() : props.handleFileUpload())}
              className={classNames(
                'ui-chip flex-shrink-0',
                props.brochure?.status === 'processing' && 'text-accent border-accent/40 ui-pulsing',
                props.brochure?.status === 'ready' && 'text-accent border-accent/40',
              )}
              aria-label={brochureEnabled ? 'Attach an image or brochure PDF' : 'Attach an image'}
            >
              <span className="i-ph:paperclip text-[13px]" />
              {/* On the hero the label is the whole point: an unlabelled clip is
                  why nobody found brochure-to-site. Mid-chat the toolbar is
                  crowded and the icon has earned its meaning, so it stays bare. */}
              {brochureEnabled && <span className="hidden sm:inline">Image or PDF</span>}
            </button>
          </Tooltip>

          {brochureEnabled && (
            <UploadIntentDialog
              isOpen={!!pendingPdf}
              fileName={pendingPdf?.name ?? ''}
              defaultIntent="brochure"
              onClose={() => setPendingPdf(null)}
              onChoose={resolveIntent}
            />
          )}

          {/* Microphone — while recording the chip goes red with a pulsing dot and a
              "Listening…" label so the live-mic state is impossible to miss. */}
          <Tooltip content={props.isListening ? 'Stop voice input' : 'Voice input'}>
            <button
              type="button"
              onClick={props.isListening ? props.stopListening : props.startListening}
              className={classNames(
                'ui-chip flex-shrink-0',
                props.isListening && 'text-danger border-danger/40 bg-danger/[0.08] hover:border-danger/60',
              )}
              aria-label={props.isListening ? 'Stop voice input' : 'Voice input'}
              aria-pressed={props.isListening}
            >
              {props.isListening ? (
                <>
                  <span className="ui-dot ui-dot-error ui-pulsing" />
                  <span className="font-medium">Listening…</span>
                  <span className="i-ph:stop-fill text-[11px]" />
                </>
              ) : (
                <span className="i-ph:microphone text-[13px]" />
              )}
            </button>
          </Tooltip>

          {/* Enhance prompt (only when not chat-started and handler exists) */}
          {!props.chatStarted && props.enhancePrompt && (
            <Tooltip content="Enhance prompt with AI">
              <button
                type="button"
                onClick={props.enhancePrompt}
                disabled={props.enhancingPrompt || !props.input.trim()}
                className={classNames(
                  'ui-chip flex-shrink-0',
                  props.enhancingPrompt && 'text-accent border-accent/40 ui-pulsing',
                  !props.input.trim() && 'opacity-50 cursor-not-allowed',
                )}
                aria-label="Enhance prompt"
              >
                <span className="i-ph:magic-wand text-[13px]" />
              </button>
            </Tooltip>
          )}

          {/* Model is locked server-side — no user-facing model selector. */}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Keyboard shortcut hint — only shown once user has typed something
              (so it appears just before they'd press Enter, not as ambient noise). */}
          {hasInput && (
            <kbd
              className="ui-kbd hidden sm:inline-flex mr-2 opacity-60 flex-shrink-0 touch:!hidden"
              aria-label="Press Enter to send"
              title="Press Enter to send"
            >
              ↵
            </kbd>
          )}

          {/* Resume — shown when the previous generation was stopped */}
          {props.canResume && !props.isStreaming && !props.isStartingChat && (
            <Tooltip content="Resume the stopped generation">
              <button
                type="button"
                className="ui-btn-accent flex-shrink-0 mr-2"
                style={{ padding: '6px 14px' }}
                onClick={(event) => {
                  event.preventDefault();
                  props.handleResume?.();
                }}
              >
                <span className="i-ph:play-fill text-[13px]" />
                <span>Resume</span>
              </button>
            </Tooltip>
          )}

          {/* Send button */}
          <Tooltip
            content={props.isStartingChat ? 'Starting…' : props.isStreaming ? 'Stop generating' : 'Send message'}
          >
            <button
              type="button"
              // min-h keeps the button the same size when its label collapses to a
              // spinner on Send, so the toolbar doesn't jump (touch raises it to 40px).
              className={classNames(
                'ui-btn-accent flex-shrink-0 min-h-8',
                !hasInput && !props.isStreaming && 'opacity-60',
              )}
              style={{ padding: '6px 14px' }}
              disabled={sendDisabled}
              onClick={(event) => {
                event.preventDefault();

                if (props.isStreaming) {
                  props.handleStop?.();
                  return;
                }

                if (hasInput) {
                  props.handleSendMessage?.(event);
                }
              }}
            >
              {props.isStartingChat ? (
                <>
                  <span className="i-ph:spinner-gap text-[13px] ui-spin" />
                </>
              ) : props.isStreaming ? (
                <>
                  <span className="i-ph:stop-fill text-[13px]" />
                  <span>Stop</span>
                </>
              ) : (
                <>
                  <span>Send</span>
                  <span className="i-ph:arrow-right text-[13px]" />
                </>
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Hidden functional components */}
      <ScreenshotStateManager
        setUploadedFiles={props.setUploadedFiles}
        setImageDataList={props.setImageDataList}
        uploadedFiles={props.uploadedFiles}
        imageDataList={props.imageDataList}
      />
      <ExpoQrModal open={props.qrModalOpen} onClose={() => props.setQrModalOpen(false)} />
    </div>
  );
};

// ─── Typing placeholder animation ───

function TypingPlaceholder({ activeTab }: { activeTab: string }) {
  const [text, setText] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const phrases = TAB_PLACEHOLDERS[activeTab] || TAB_PLACEHOLDERS.landing;

  useEffect(() => {
    // Reset when tab changes
    setText('');
    setPhraseIndex(0);
    setCharIndex(0);
    setIsDeleting(false);
  }, [activeTab]);

  useEffect(() => {
    const currentPhrase = phrases[phraseIndex % phrases.length];

    if (!isDeleting && charIndex <= currentPhrase.length) {
      // Typing forward
      const timeout = setTimeout(
        () => {
          setText(currentPhrase.slice(0, charIndex));
          setCharIndex((c) => c + 1);
        },
        40 + Math.random() * 30,
      ); // Slightly randomized for natural feel

      return () => clearTimeout(timeout);
    } else if (!isDeleting && charIndex > currentPhrase.length) {
      // Pause at end of phrase
      const timeout = setTimeout(() => setIsDeleting(true), 2000);
      return () => clearTimeout(timeout);
    } else if (isDeleting && charIndex > 0) {
      // Deleting backward (faster)
      const timeout = setTimeout(() => {
        setCharIndex((c) => c - 1);
        setText(currentPhrase.slice(0, charIndex - 1));
      }, 20);

      return () => clearTimeout(timeout);
    } else if (isDeleting && charIndex === 0) {
      // Move to next phrase
      setIsDeleting(false);
      setPhraseIndex((i) => (i + 1) % phrases.length);
    }

    return undefined;
  }, [charIndex, isDeleting, phraseIndex, phrases]);

  return (
    <div className="absolute inset-0 pointer-events-none px-1 py-0.5 flex items-start">
      <span className="font-geist text-[13px] leading-[1.55] text-muted">
        {text}
        <span className="inline-block w-[2px] h-[14px] bg-muted ml-[1px] align-middle animate-pulse" />
      </span>
    </div>
  );
}
